import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import colors from '@general/colors';

// Styled component for the third-level subnav item
const Item = styled.li`
    font-size: 14px;
    margin: 18px 0 18px 0;
    list-style-type: none; /* Removes the bullet point */

    &::marker {
        display: none; /* Hides default browser markers */
    }

    a {
        text-decoration: none;
        color: ${props => (props.active ? colors.primary : colors.black)};
        font-weight: ${props => (props.active ? 'bold' : 'normal')};
        &:hover,
        &:focus {
            color: ${colors.primary};
            text-decoration: underline;
        }
    }

    .icon {
        margin-right: 8px; /* Adjusted margin for the icon */
    }
`;

// SVG for the icon used in third-level subnav items
const SubnavIcon = () => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0.630859 1V5C0.630859 7.20914 2.42172 9 4.63086 9H8.63086"
            stroke="#4A4A4A"
            strokeLinecap="round"
        />
    </svg>
);

const ThirdLevelSubnavItem = ({ label, to, children }) => {
    const location = useLocation();
    const history = useHistory();
    const active = location.pathname === to;

    // Automatically redirect to the first child or grandchild when active
    useEffect(() => {
        if (active && children) {
            const firstChild = React.Children.toArray(children)[0];
            const grandChildren = React.Children.toArray(firstChild?.props?.children);
            if (grandChildren?.length && location.pathname === to) {
                history.push(grandChildren[0].props.to); // Redirect to the first grandchild
            } else if (firstChild?.props?.to && location.pathname === to) {
                history.push(firstChild.props.to); // Redirect to the first child
            }
        }
    }, [active, children, to, location.pathname, history]);

    return (
        <Item active={active}>
            <Link to={to}>
                <span className="icon">
                    <SubnavIcon />
                </span>
                {label}
            </Link>
            {children}
        </Item>
    );
};

ThirdLevelSubnavItem.propTypes = {
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    children: PropTypes.node,
};

export default ThirdLevelSubnavItem;
